import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, ROLES } from '../contexts/AuthContext';
import { visitsAPI, clientsAPI, staffAPI, geocodeAPI } from '../lib/api';
import { formatDate, formatTime, getStatusClass, capitalize } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Calendar,
  MapPin,
  User,
  Plus, Trash2,
  Loader2,
  Search,
  Filter,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';
import AddressAutocomplete from '../components/common/AddressAutocomplete';

const SERVICE_TYPES = [
  'Community Access',
  'Personal Care',
  'Domestic Assistance',
  'Transport',
  'Social Support',
  'Respite Care',
  'Other'
];

export default function Visits() {
  const { user, canManage, isStaff } = useAuth();
  const navigate = useNavigate();
  const [visits, setVisits] = useState([]);
  const [clients, setClients] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [filter, setFilter] = useState({ status: '' });
  const [searchQuery, setSearchQuery] = useState('');

  const [newVisit, setNewVisit] = useState({
    client_id: '',
    staff_id: '',
    service_type: '',
    date: new Date().toISOString().split('T')[0],
    start_time: '09:00',
    end_time: '10:00',
    address: '',
    notes: '',
      geofence_radius_m: 100,
      site_latitude: '',
      site_longitude: ''
  });

const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (filter.status && filter.status !== 'all') {
        params.status = filter.status;
      }
      
      const [visitsRes, clientsRes] = await Promise.all([
        visitsAPI.getAll(params),
        clientsAPI.getAll()
      ]);
      setVisits(visitsRes.data);
      setClients(clientsRes.data);

      if (canManage) {
        const staffRes = await staffAPI.getAll({ role: ROLES.STAFF });
        setStaffList(staffRes.data);
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load visits');
    } finally {
      setLoading(false);
    }
  }, [filter.status, canManage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateVisit = async (e) => {
    e.preventDefault();
    if (!newVisit.client_id || !newVisit.staff_id || !newVisit.service_type) {
      toast.error('Please fill in all required fields');
      return;
    }

    setCreating(true);
    try {
      const visitPayload = {
        ...newVisit,
        geofence_radius_m: Number(newVisit.geofence_radius_m || 100),
        site_latitude: newVisit.site_latitude ? Number(newVisit.site_latitude) : null,
        site_longitude: newVisit.site_longitude ? Number(newVisit.site_longitude) : null,
      };
      await visitsAPI.create(visitPayload);
      toast.success('Visit created successfully');
      setCreateDialogOpen(false);
      setNewVisit({
        client_id: '',
        staff_id: '',
        service_type: '',
        date: new Date().toISOString().split('T')[0],
        start_time: '09:00',
        end_time: '10:00',
        address: '',
        notes: '',
      geofence_radius_m: 100,
      site_latitude: '',
      site_longitude: ''
      });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create visit');
    } finally {
      setCreating(false);
    }
  };

  // Auto-fill address when client is selected
  const handleClientChange = (clientId) => {
    setNewVisit(prev => ({ ...prev, client_id: clientId }));
    const client = clients.find(c => c.id === clientId);
    if (client?.address) {
      setNewVisit(prev => ({ ...prev, address: client.address }));
    }
  };

  const handleDeleteVisit = async (visitId) => {
    if (!window.confirm('Delete this visit/shift? This cannot be undone.')) return;

    try {
      await visitsAPI.delete(visitId);
      toast.success('Visit deleted successfully');
      window.location.href = '/visits';
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete visit');
    }
  };

  const handleDeleteAllVisits = async () => {
    if (!window.confirm('Delete ALL existing visits and related clock/mileage/notes records? This cannot be undone.')) return;

    try {
      await visitsAPI.deleteAll();
      fetchVisits();
      toast.success('All visits deleted successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete all visits');
    }
  };

  const filteredVisits = visits.filter(visit => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        visit.client_name?.toLowerCase().includes(query) ||
        visit.staff_name?.toLowerCase().includes(query) ||
        visit.service_type?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-secondary" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="visits-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="font-heading text-2xl font-bold text-foreground">
          {isStaff ? 'My Visits' : 'Visits'}
        </h1>
        {canManage && (
          <div className="flex gap-2">
            <Button
              type="button"
              variant="destructive"
              onClick={handleDeleteAllVisits}
            >
              Delete All Visits
            </Button>

            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-secondary hover:bg-secondary/90" data-testid="create-visit-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Visit
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create New Visit</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateVisit} className="space-y-4 pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Client *</Label>
                    <Select value={newVisit.client_id} onValueChange={handleClientChange}>
                      <SelectTrigger data-testid="visit-client-select">
                        <SelectValue placeholder="Select client" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map(client => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Staff *</Label>
                    <Select 
                      value={newVisit.staff_id} 
                      onValueChange={(v) => setNewVisit(prev => ({ ...prev, staff_id: v }))}
                    >
                      <SelectTrigger data-testid="visit-staff-select">
                        <SelectValue placeholder="Select staff" />
                      </SelectTrigger>
                      <SelectContent>
                        {staffList.map(staff => (
                          <SelectItem key={staff.id} value={staff.id}>
                            {staff.full_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Service Type *</Label>
                  <Select 
                    value={newVisit.service_type} 
                    onValueChange={(v) => setNewVisit(prev => ({ ...prev, service_type: v }))}
                  >
                    <SelectTrigger data-testid="visit-service-select">
                      <SelectValue placeholder="Select service type" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICE_TYPES.map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Date *</Label>
                    <Input
                      type="date"
                      value={newVisit.date}
                      onChange={(e) => setNewVisit(prev => ({ ...prev, date: e.target.value }))}
                      data-testid="visit-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Start Time *</Label>
                    <Input
                      type="time"
                      value={newVisit.start_time}
                      onChange={(e) => setNewVisit(prev => ({ ...prev, start_time: e.target.value }))}
                      data-testid="visit-start-time-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Time *</Label>
                    <Input
                      type="time"
                      value={newVisit.end_time}
                      onChange={(e) => setNewVisit(prev => ({ ...prev, end_time: e.target.value }))}
                      data-testid="visit-end-time-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Address *</Label>
                  <AddressAutocomplete
                    value={newVisit.address}
                    onAddressChange={(address) =>
                      setNewVisit((prev) => ({
                        ...prev,
                        address,
                      }))
                    }
                    onSelect={(location) =>
                      setNewVisit((prev) => ({
                        ...prev,
                        address: location.address,
                        site_latitude: String(location.latitude),
                        site_longitude: String(location.longitude),
                      }))
                    }
                  />
                </div>

                
                  <div className="space-y-2">
                    <Label>Geofence Radius</Label>
                    <Select
                      value={String(newVisit.geofence_radius_m || 100)}
                      onValueChange={(value) => setNewVisit({ ...newVisit, geofence_radius_m: Number(value) })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select geofence radius" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="50">50 metres - Strict</SelectItem>
                        <SelectItem value="100">100 metres - Standard</SelectItem>
                        <SelectItem value="200">200 metres - Flexible</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  
                  <div className="md:col-span-2">
<p className="mt-2 text-xs text-muted-foreground">
                      This automatically fills latitude and longitude for geofence clock-in validation.
                    </p>
                  </div>

<div className="space-y-2">
                    <Label>Site Latitude</Label>
                    <Input
                      type="number"
                      step="any"
                      placeholder="-32.9283"
                      value={newVisit.site_latitude || ''}
                      readOnly
                      className="bg-slate-950 text-slate-100 border-slate-700 placeholder:text-slate-400"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Site Longitude</Label>
                    <Input
                      type="number"
                      step="any"
                      placeholder="151.7817"
                      value={newVisit.site_longitude || ''}
                      readOnly
                      className="bg-slate-950 text-slate-100 border-slate-700 placeholder:text-slate-400"
                    />
                  </div>

<div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={newVisit.notes}
                    onChange={(e) => setNewVisit(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Instructions for the staff member"
                    rows={3}
                    data-testid="visit-notes-input"
                  />
                </div>

                <Button type="submit" className="w-full" disabled={creating} data-testid="submit-visit-btn">
                  {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Create Visit
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search visits..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="search-visits-input"
          />
        </div>
        <Select value={filter.status} onValueChange={(v) => setFilter(prev => ({ ...prev, status: v }))}>
          <SelectTrigger className="w-full sm:w-40" data-testid="filter-status-select">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="upcoming">Upcoming</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Visits List */}
      {filteredVisits.length > 0 ? (
        <div className="space-y-3">
          {filteredVisits.map(visit => (
            <Card 
              key={visit.id} 
              className="visit-card card-interactive"
              onClick={() => navigate(`/visits/${visit.id}`)}
              data-testid={`visit-card-${visit.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-secondary" />
                      <span className="font-semibold">{visit.client_name}</span>
                      <Badge className={getStatusClass(visit.status)} variant="secondary">
                        {capitalize(visit.status)}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Calendar className="w-4 h-4" />
                      <span>{formatDate(visit.date)}</span>
                      <Clock className="w-4 h-4 ml-2" />
                      <span>{formatTime(visit.start_time)} - {formatTime(visit.end_time)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <MapPin className="w-4 h-4" />
                      <span className="truncate">{visit.address}</span>
                    </div>
                    {canManage && (
                      <p className="text-sm text-muted-foreground">
                        Assigned to: {visit.staff_name}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                        <Badge variant="outline">{visit.service_type}</Badge>
                        {canManage && (
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDeleteVisit(visit.id)}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Delete Visit
                          </Button>
                        )}
                      </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="empty-state py-12">
            <Calendar className="empty-state-icon" />
            <p className="empty-state-title">No visits found</p>
            <p className="empty-state-description">
              {canManage 
                ? 'Create a new visit to get started' 
                : 'No visits have been assigned to you yet'}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
